import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Droppable } from 'react-beautiful-dnd';
import { Box } from '@mui/material';
import { NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { Icon } from '@pwc/pwc-deals-ui';
import { Loader } from 'common/components/Loader.tsx';
import '../../../../common/components/ContentActions/RowActionsButton.scss';
import './AnalysesKfaList.scss';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import AnalysisItemDraggable from '../AnalysisItemDraggable/AnalysisItemDraggable';
import {
  getContentMessage,
  geKFAtNavigationLink,
} from '../../utils/groupsUtilities.ts';
import { GROUP_PAGE_SIZE } from '../../models/constants.ts';
import useGroupsPermissions from '../../utils/useGroupsPermissions.ts';
import LoadMoreAnalysesButton from '../LoadMoreAnalysesButton/LoadMoreAnalysesButton';
import { useKFATitleI18n } from '../../../../common/hooks/useKFATitleI18n';

const AnalysesKfaList = ({
  group,
  projectId,
  list = [],
  setList,
  loadPaginatedData,
  handleDeleteAnalyses,
  handleSelect,
  tags,
  dragInProgress,
  sourceId,
  permissions,
  selectedIDs,
  enableDragAndDrop,
}) => {
  const { title, contentCount, contentId, contentThreadId } = group;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const { hasPermission } = useGroupsPermissions(projectId);

  const droppableId = (title || '').toLowerCase().split(' ').join('-');

  const toggleCollapsible = () => setOpen(!open);
  const { t } = useTranslation();

  const handleKeyPress = ({ code }) => {
    if (code === 'Space') {
      toggleCollapsible();
    }
  };

  const handleLoadMore = () => {
    setPage(page + 1);
  };

  const showLoadMore = useMemo(
    () => !loading && list.length !== contentCount && contentCount > 0,
    [contentCount, list.length, loading]
  );

  const showLoader = useMemo(
    () => list.length !== contentCount && loading,
    [list.length, contentCount, loading]
  );

  const arrowType = useMemo(() => {
    return open ? 'desc' : 'down-chevron';
  }, [open]);

  /* Load analyses on first open and only if necessary. */
  useEffect(() => {
    const getAnalysesList = async () => {
      const response = await loadPaginatedData(
        projectId,
        contentId,
        page,
        GROUP_PAGE_SIZE,
        tags
      );

      setLoading(false);
      const items = response.items ? response.items : response;
      setList(contentId, [...list, ...items]);
    };

    if (open && contentCount > 0 && list.length === 0) {
      setLoading(true);
      getAnalysesList();
    }
    // eslint-disable-next-line
  }, [open]);

  /* Load more analyses on page change. */
  useEffect(() => {
    const getAnalysesList = async () => {
      const response = await loadPaginatedData(
        projectId,
        contentId,
        page,
        GROUP_PAGE_SIZE
      );

      setLoading(false);
      const items = response.items ? response.items : response;
      setList(contentId, [...list, ...items]);
    };

    if (page > 0) {
      setLoading(true);
      getAnalysesList();
    }
    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    setOpen(false);
  }, [tags]);

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);
  const KFATitleI18n = useKFATitleI18n(KFATitle);

  return (
    <div className="analyses-kfa-list" data-testid="collapse-group-kfa">
      <div className="analyses-kfa-list__layout">
        <div className="analyses-kfa-list__header">
          <div
            className="analyses-kfa-list__layout__open-indicator"
            onKeyDown={handleKeyPress}
            onClick={toggleCollapsible}
            role="button"
            tabIndex={0}
          >
            <Icon name={arrowType} width="24" className="icon-orange" />
          </div>
          <div
            className="analyses-kfa-list__layout__title-section"
            onKeyDown={handleKeyPress}
            onClick={toggleCollapsible}
            role="button"
            tabIndex={0}
          >
            <span className="analyses-kfa-list__layout__title-section__title">
              {contentId !== -1
                ? title
                : t('otherAnalysesNotConnectedToAKfa', {
                    KFATitle: KFATitleI18n,
                  })}
            </span>
            <div className="content-count">
              {getContentMessage(contentCount, t)}
            </div>
          </div>
        </div>

        {contentId !== -1 ? (
          <div className="analyses-kfa-list__layout__controls">
            <Link
              className="analyses-kfa-list__view-kfa__link"
              to={`${geKFAtNavigationLink(projectId, contentThreadId)}`}
            >
              {t('viewContent', { contentName: KFATitleI18n })}
            </Link>
          </div>
        ) : (
          ''
        )}
      </div>

      <Droppable
        key={droppableId}
        droppableId={droppableId}
        isDropDisabled={dragInProgress && droppableId !== sourceId}
      >
        {(provided) => {
          return (
            <div
              style={{
                height: open ? '100%' : 0,
                minHeight: open && dragInProgress ? '110px' : 'auto',
              }}
              key={droppableId}
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="analyses-custom-list__content"
            >
              <div
                style={{
                  visibility: open ? 'visible' : 'hidden',
                  marginTop: open ? 'var(--s4)' : '0',
                }}
              >
                <div className="analyses-kfa-list__content">
                  {contentCount > 0 ? (
                    list.map((analysis, index) => (
                      <AnalysisItemDraggable
                        groupId={contentThreadId}
                        key={analysis.contentThreadId}
                        index={index}
                        projectId={projectId}
                        contentId={contentId}
                        item={analysis}
                        hasPermission={hasPermission}
                        handleDelete={handleDeleteAnalyses}
                        permissions={permissions}
                        selectedIDs={selectedIDs}
                        handleSelect={handleSelect}
                        enableDragAndDrop={enableDragAndDrop}
                        isKfa
                        showConnectedKfa={false}
                        dragInProgress={dragInProgress}
                      />
                    ))
                  ) : (
                    <div className="analyses-kfa-list__content__placeholder">
                      {t('noAnalysesConnectedKfa', { KFATitle: KFATitleI18n })}
                    </div>
                  )}
                  {list && list.length > 0 && provided.placeholder}
                  {showLoadMore && (
                    <LoadMoreAnalysesButton handleLoadMore={handleLoadMore} />
                  )}
                  {showLoader && (
                    <Box m="1rem" textAlign="center">
                      <Loader />
                    </Box>
                  )}
                </div>
              </div>
            </div>
          );
        }}
      </Droppable>
    </div>
  );
};

export default AnalysesKfaList;
